import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  AlertTriangle,
  Sliders,
  RefreshCw,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';

/**
 * /alternatives/cashflow-forecast
 *
 * PE Phase 4 deliverable 6 — Takahashi-Alexander cash-flow forecasting.
 *
 * <p>Hybrid landing + detail UX:
 * <ul>
 *   <li><b>Landing</b> (PGIM-style): scope picker (commitment | portfolio),
 *       parameter strip with the six T-A knobs, KPI rail, projected calls /
 *       distributions / NAV chart, full quarterly series table.</li>
 *   <li><b>Slide-over</b> (Aladdin-style): "Tune parameters" opens an inline
 *       sliders panel — fund life, investment period, bow shapes, growth,
 *       yield. Re-running the forecast is one click.</li>
 * </ul>
 */

interface Portfolio {
  id: number;
  portfolioId: number;
  portfolioName: string;
}

interface Commitment {
  id: number;
  fundId: number | null;
  portfolioId: number;
  commitmentAmount: number;
  commitmentCurrency: string;
  fundName?: string;
  managerName?: string;
}

interface ForecastBucket {
  bucketDate: string;
  ageYears: number;
  capitalCall: number;
  distribution: number;
  netCashFlow: number;
  nav: number;
  cumulativeCalled: number;
  cumulativeDistributed: number;
}

interface ForecastResp {
  scope: 'COMMITMENT' | 'PORTFOLIO';
  commitmentId?: number;
  portfolioId?: number;
  fundName?: string;
  vintageYear?: number;
  commitmentAmount?: number;
  totalCommitted?: number;
  commitmentCount?: number;
  currency?: string;
  series: ForecastBucket[];
  totals: {
    forecastedCalls: number;
    forecastedDistributions: number;
    forecastedNetCashFlow: number;
    peakProjectedNav: number;
  };
  parameters: TAParams;
}

interface TAParams {
  fundLife: number;
  investmentPeriod: number;
  bowContribution: number;
  bowDistribution: number;
  growthRate: number;
  yieldRate: number;
  horizonQuarters: number;
}

const DEFAULT_PARAMS: TAParams = {
  fundLife: 12,
  investmentPeriod: 5,
  bowContribution: 2.0,
  bowDistribution: 2.5,
  growthRate: 0.13,
  yieldRate: 0.0,
  horizonQuarters: 48,
};

const fmtMoney = (v: number | null | undefined) => {
  if (v == null || isNaN(v as number)) return '—';
  const n = Number(v);
  const a = Math.abs(n);
  if (a >= 1e9) return `${n < 0 ? '-' : ''}$${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${n < 0 ? '-' : ''}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${n < 0 ? '-' : ''}$${(a / 1e3).toFixed(0)}K`;
  return `${n < 0 ? '-' : ''}$${a.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const fmtPct = (decimal: number | null | undefined, digits = 1) => {
  if (decimal == null || isNaN(decimal as number)) return '—';
  return `${(Number(decimal) * 100).toFixed(digits)}%`;
};

interface CashFlowForecastScreenProps {
  /**
   * When rendered as a sub-tab inside AlternativesTab we drop the back button
   * + outer min-height/padding chrome so the screen sits flush in the embed.
   */
  embedded?: boolean;
}

const CashFlowForecastScreen: React.FC<CashFlowForecastScreenProps> = ({ embedded = false }) => {
  const navigate = useNavigate();

  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [commitments, setCommitments] = useState<Commitment[]>([]);
  const [scope, setScope] = useState<'PORTFOLIO' | 'COMMITMENT'>('PORTFOLIO');
  const [portfolioId, setPortfolioId] = useState<number | null>(null);
  const [commitmentId, setCommitmentId] = useState<number | null>(null);
  const [params, setParams] = useState<TAParams>(DEFAULT_PARAMS);
  const [forecast, setForecast] = useState<ForecastResp | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showParams, setShowParams] = useState(false);

  // Load portfolios once
  useEffect(() => {
    axios
      .get('/api/dimensions/portfolios')
      .then((r) => {
        const list = r.data?.content || r.data || [];
        const norm: Portfolio[] = list.map((p: any) => ({
          id: p.id ?? p.portfolioId,
          portfolioId: p.portfolioId ?? p.id,
          portfolioName: p.portfolioName || p.name || `Portfolio #${p.id}`,
        }));
        setPortfolios(norm);
        if (norm.length && portfolioId == null) setPortfolioId(norm[0].id);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load commitments when portfolio changes (for commitment-level scope)
  useEffect(() => {
    if (portfolioId == null) {
      setCommitments([]);
      return;
    }
    axios
      .get(`/api/alternatives/commitments/portfolio/${portfolioId}`)
      .then((r) => {
        const list: Commitment[] = Array.isArray(r.data) ? r.data : [];
        setCommitments(list);
        if (list.length && commitmentId == null) setCommitmentId(list[0].id);
      })
      .catch(() => setCommitments([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId]);

  const runForecast = () => {
    setLoading(true);
    setError(null);
    setForecast(null);
    const url =
      scope === 'PORTFOLIO'
        ? `/api/alternatives/cashflow-forecast/portfolio/${portfolioId}`
        : `/api/alternatives/cashflow-forecast/commitments/${commitmentId}`;
    axios
      .post(url, params)
      .then((r) => setForecast(r.data as ForecastResp))
      .catch((err) =>
        setError(err?.response?.data?.message || err?.message || 'Forecast failed')
      )
      .finally(() => setLoading(false));
  };

  // Auto-run on first valid selection
  useEffect(() => {
    if (scope === 'PORTFOLIO' && portfolioId != null) runForecast();
    if (scope === 'COMMITMENT' && commitmentId != null) runForecast();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, portfolioId, commitmentId]);

  // Chart geometry
  const chart = useMemo(() => buildChart(forecast?.series || []), [forecast]);

  return (
    <div
      className={
        embedded
          ? 'text-slate-100'
          : 'min-h-screen bg-slate-950 text-slate-100 px-6 py-6'
      }
    >
      <div className={embedded ? '' : 'max-w-[1400px] mx-auto'}>
        {!embedded && (
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white mb-4"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back
          </button>
        )}

        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-violet-400 font-bold mb-1">
              Cash-flow forecast
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight">
              Takahashi-Alexander projections
            </h1>
            <p className="text-sm text-slate-400 mt-1 max-w-3xl">
              Parametric forecast of remaining capital calls, distributions, and NAV. The model
              is anchored on vintage (age 0); the chart and table show forward-only periods.
              Sliders flex assumptions per your market view — investment pace, distribution shape,
              NAV growth, current yield. Pure projection: complements the J-curve, which
              backfits realised history.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowParams((s) => !s)}
              className="bg-slate-800 hover:bg-slate-700 text-white px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 border border-white/10"
            >
              <Sliders className="w-4 h-4" />
              {showParams ? 'Hide params' : 'Tune params'}
            </button>
            <button
              onClick={runForecast}
              disabled={
                loading ||
                (scope === 'PORTFOLIO' && portfolioId == null) ||
                (scope === 'COMMITMENT' && commitmentId == null)
              }
              className="bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 shadow-lg shadow-violet-600/20"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Forecasting…' : 'Run forecast'}
            </button>
          </div>
        </div>

        {/* Scope picker */}
        <div className="bg-slate-900/40 border border-white/5 rounded-xl px-5 py-4 mb-3 flex items-center gap-4 flex-wrap">
          <div className="flex bg-slate-950 rounded-lg p-0.5 border border-white/10">
            {(['PORTFOLIO', 'COMMITMENT'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setScope(s)}
                className={`px-3 py-1.5 text-xs font-semibold rounded ${
                  scope === s
                    ? 'bg-violet-600 text-white'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {s === 'PORTFOLIO' ? 'Portfolio aggregate' : 'Single commitment'}
              </button>
            ))}
          </div>

          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            Portfolio
          </div>
          <select
            value={portfolioId ?? ''}
            onChange={(e) => setPortfolioId(e.target.value ? Number(e.target.value) : null)}
            className="bg-slate-950 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-violet-500"
          >
            <option value="">— Select portfolio —</option>
            {portfolios.map((p) => (
              <option key={p.id} value={p.id}>
                P{p.portfolioId} • {p.portfolioName}
              </option>
            ))}
          </select>

          {scope === 'COMMITMENT' && (
            <>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                Commitment
              </div>
              <select
                value={commitmentId ?? ''}
                onChange={(e) =>
                  setCommitmentId(e.target.value ? Number(e.target.value) : null)
                }
                className="bg-slate-950 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-violet-500 min-w-[260px]"
                disabled={commitments.length === 0}
              >
                {commitments.length === 0 ? (
                  <option value="">— No commitments in portfolio —</option>
                ) : (
                  <>
                    <option value="">— Select commitment —</option>
                    {commitments.map((c) => (
                      <option key={c.id} value={c.id}>
                        #{c.id} • {c.fundName || `Fund ${c.fundId}`} •{' '}
                        {fmtMoney(c.commitmentAmount)}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </>
          )}
        </div>

        {/* Param sliders */}
        {showParams && (
          <ParamPanel params={params} onChange={setParams} onReset={() => setParams(DEFAULT_PARAMS)} />
        )}

        {error && (
          <div className="bg-rose-500/10 border border-rose-500/30 text-rose-300 rounded-lg px-4 py-3 mb-4 text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {error}
          </div>
        )}

        {/* KPI rail */}
        {forecast && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-5 mt-3">
            <Kpi
              label="Scope"
              value={
                forecast.scope === 'PORTFOLIO'
                  ? `${forecast.commitmentCount ?? 0} commit${
                      forecast.commitmentCount === 1 ? '' : 's'
                    }`
                  : forecast.fundName || `#${forecast.commitmentId}`
              }
            />
            <Kpi
              label={forecast.scope === 'PORTFOLIO' ? 'Total committed' : 'Commitment'}
              value={fmtMoney(
                forecast.scope === 'PORTFOLIO'
                  ? forecast.totalCommitted
                  : forecast.commitmentAmount
              )}
            />
            <Kpi
              label="Forecasted calls"
              value={fmtMoney(forecast.totals.forecastedCalls)}
              tone="warning"
            />
            <Kpi
              label="Forecasted distributions"
              value={fmtMoney(forecast.totals.forecastedDistributions)}
              tone="positive"
            />
            <Kpi
              label="Net cash flow"
              value={fmtMoney(forecast.totals.forecastedNetCashFlow)}
              tone={
                forecast.totals.forecastedNetCashFlow >= 0 ? 'positive' : 'warning'
              }
            />
            <Kpi label="Peak NAV" value={fmtMoney(forecast.totals.peakProjectedNav)} />
          </div>
        )}

        {/* Chart */}
        <div className="bg-slate-900/40 border border-white/5 rounded-xl px-5 py-4 mb-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-white">
              Quarterly projection
              {forecast && forecast.series.length > 0 && (
                <span className="text-slate-500 font-normal ml-2 text-xs">
                  {forecast.series.length} forward periods • through{' '}
                  {forecast.series[forecast.series.length - 1].bucketDate}
                </span>
              )}
            </h2>
            <div className="flex items-center gap-3 text-[10px] text-slate-400">
              <Legend color="bg-amber-400" label="Capital call" />
              <Legend color="bg-emerald-400" label="Distribution" />
              <Legend color="bg-violet-400" label="NAV" />
            </div>
          </div>
          {!forecast ? (
            <div className="px-5 py-12 text-center text-slate-500 text-sm">
              {loading ? 'Forecasting…' : 'Pick a scope above to run a forecast.'}
            </div>
          ) : forecast.series.length === 0 ? (
            <div className="px-5 py-12 text-center text-slate-500 text-sm">
              No forward periods — series may be fully past horizon.
            </div>
          ) : (
            <ProjectionChart geom={chart} series={forecast.series} />
          )}
        </div>

        {/* Series table */}
        {forecast && forecast.series.length > 0 && (
          <div className="bg-slate-900/40 border border-white/5 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-white">Forecast series</h2>
              <div className="text-xs text-slate-500">
                {forecast.series.length} quarterly buckets
              </div>
            </div>
            <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-950/80 sticky top-0 z-10">
                  <tr className="text-slate-500 uppercase tracking-wider text-[10px]">
                    <th className="text-left px-5 py-2 font-semibold">Date</th>
                    <th className="text-right px-3 py-2 font-semibold">Age (yrs)</th>
                    <th className="text-right px-3 py-2 font-semibold">Capital call</th>
                    <th className="text-right px-3 py-2 font-semibold">Distribution</th>
                    <th className="text-right px-3 py-2 font-semibold">Net CF</th>
                    <th className="text-right px-3 py-2 font-semibold">NAV</th>
                    <th className="text-right px-3 py-2 font-semibold">Cum called</th>
                    <th className="text-right px-5 py-2 font-semibold">Cum distrib</th>
                  </tr>
                </thead>
                <tbody>
                  {forecast.series.map((b, i) => (
                    <tr key={i} className="border-t border-white/5 hover:bg-white/5">
                      <td className="px-5 py-2 text-slate-200">{b.bucketDate}</td>
                      <td className="px-3 py-2 text-right text-slate-400 font-mono">
                        {Number(b.ageYears).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-amber-300">
                        {fmtMoney(b.capitalCall)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-300">
                        {fmtMoney(b.distribution)}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono font-semibold ${
                          Number(b.netCashFlow) >= 0 ? 'text-emerald-300' : 'text-amber-300'
                        }`}
                      >
                        <span className="inline-flex items-center gap-1 justify-end">
                          {Number(b.netCashFlow) >= 0 ? (
                            <TrendingUp className="w-3 h-3" />
                          ) : (
                            <TrendingDown className="w-3 h-3" />
                          )}
                          {fmtMoney(b.netCashFlow)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-violet-300">
                        {fmtMoney(b.nav)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-400">
                        {fmtMoney(b.cumulativeCalled)}
                      </td>
                      <td className="px-5 py-2 text-right font-mono text-slate-400">
                        {fmtMoney(b.cumulativeDistributed)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="mt-6 text-xs text-slate-500 max-w-3xl">
          <strong className="text-slate-400">How it works.</strong> Cumulative contributions
          follow{' '}
          <code className="bg-slate-800 px-1 py-0.5 rounded text-slate-300">
            RC(t) = 1 − (1 − t/I)^B_C
          </code>
          ; quarterly distribution rate{' '}
          <code className="bg-slate-800 px-1 py-0.5 rounded text-slate-300">
            RD(t) = max(yield, (t/L)^B_D)
          </code>{' '}
          applied to prior NAV. NAV grows at{' '}
          <code className="bg-slate-800 px-1 py-0.5 rounded text-slate-300">
            (1+G)^0.25
          </code>{' '}
          per quarter, net of calls and distributions. Forward-only — past quarters are not in the
          series.
        </div>
      </div>
    </div>
  );
};

// ---- Sub-components ---------------------------------------------------------

const Kpi: React.FC<{
  label: string;
  value: string;
  tone?: 'neutral' | 'positive' | 'warning';
}> = ({ label, value, tone = 'neutral' }) => {
  const color =
    tone === 'positive'
      ? 'text-emerald-300'
      : tone === 'warning'
      ? 'text-amber-300'
      : 'text-white';
  return (
    <div className="bg-slate-900/40 border border-white/5 rounded-lg px-4 py-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">
        {label}
      </div>
      <div className={`text-base font-bold ${color} truncate`} title={value}>
        {value}
      </div>
    </div>
  );
};

const Legend: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <div className="flex items-center gap-1.5">
    <span className={`w-2.5 h-2.5 rounded ${color}`} />
    <span>{label}</span>
  </div>
);

const ParamPanel: React.FC<{
  params: TAParams;
  onChange: (p: TAParams) => void;
  onReset: () => void;
}> = ({ params, onChange, onReset }) => (
  <div className="bg-slate-900/40 border border-violet-500/20 rounded-xl px-5 py-4 mb-3">
    <div className="flex items-center justify-between mb-3">
      <div className="text-[10px] uppercase tracking-widest text-violet-300 font-bold">
        Takahashi-Alexander parameters
      </div>
      <button
        onClick={onReset}
        className="text-[11px] text-slate-400 hover:text-white"
      >
        Reset to defaults
      </button>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      <Slider
        label="Fund life (yrs)"
        value={params.fundLife}
        min={5}
        max={20}
        step={0.5}
        onChange={(v) => onChange({ ...params, fundLife: v })}
        format={(v) => v.toFixed(1)}
      />
      <Slider
        label="Investment period (yrs)"
        value={params.investmentPeriod}
        min={1}
        max={10}
        step={0.5}
        onChange={(v) => onChange({ ...params, investmentPeriod: v })}
        format={(v) => v.toFixed(1)}
      />
      <Slider
        label="Bow contribution"
        value={params.bowContribution}
        min={0.5}
        max={5}
        step={0.1}
        onChange={(v) => onChange({ ...params, bowContribution: v })}
        format={(v) => v.toFixed(1)}
      />
      <Slider
        label="Bow distribution"
        value={params.bowDistribution}
        min={0.5}
        max={5}
        step={0.1}
        onChange={(v) => onChange({ ...params, bowDistribution: v })}
        format={(v) => v.toFixed(1)}
      />
      <Slider
        label="NAV growth (annual)"
        value={params.growthRate}
        min={-0.05}
        max={0.3}
        step={0.005}
        onChange={(v) => onChange({ ...params, growthRate: v })}
        format={(v) => fmtPct(v)}
      />
      <Slider
        label="Yield rate (annual)"
        value={params.yieldRate}
        min={0}
        max={0.15}
        step={0.005}
        onChange={(v) => onChange({ ...params, yieldRate: v })}
        format={(v) => fmtPct(v)}
      />
    </div>
  </div>
);

const Slider: React.FC<{
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  format: (v: number) => string;
}> = ({ label, value, min, max, step, onChange, format }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
        {label}
      </span>
      <span className="text-[11px] font-mono text-violet-300 font-semibold">
        {format(value)}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full accent-violet-500"
    />
  </div>
);

// ---- Chart ------------------------------------------------------------------

interface ChartGeom {
  width: number;
  height: number;
  padL: number;
  padR: number;
  padT: number;
  padB: number;
  cashMin: number;
  cashMax: number;
  navMin: number;
  navMax: number;
}

function buildChart(series: ForecastBucket[]): ChartGeom {
  const cashVals: number[] = [];
  const navVals: number[] = [];
  series.forEach((b) => {
    cashVals.push(Number(b.capitalCall) || 0);
    cashVals.push(-(Number(b.distribution) || 0)); // distributions plot below x-axis
    navVals.push(Number(b.nav) || 0);
  });
  const cashMax = Math.max(0, ...cashVals);
  const cashMin = Math.min(0, ...cashVals);
  const navMax = Math.max(0, ...navVals);
  return {
    width: 1320,
    height: 320,
    padL: 70,
    padR: 70,
    padT: 20,
    padB: 40,
    cashMin: cashMin === 0 && cashMax === 0 ? -1 : cashMin,
    cashMax: cashMax === 0 && cashMin === 0 ? 1 : cashMax,
    navMin: 0,
    navMax: navMax === 0 ? 1 : navMax,
  };
}

const ProjectionChart: React.FC<{ geom: ChartGeom; series: ForecastBucket[] }> = ({
  geom,
  series,
}) => {
  const { width, height, padL, padR, padT, padB, cashMin, cashMax, navMax } = geom;
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;

  const n = series.length;
  if (n === 0) return null;
  const barW = Math.max(2, innerW / n - 1);

  const xAt = (i: number) => padL + (i + 0.5) * (innerW / n);
  const cashRange = cashMax - cashMin;
  const cashY = (v: number) =>
    padT + ((cashMax - v) / (cashRange === 0 ? 1 : cashRange)) * innerH;
  const cashBaseline = cashY(0);
  const navY = (v: number) => padT + ((navMax - v) / (navMax === 0 ? 1 : navMax)) * innerH;

  // NAV path
  const navPath = series
    .map((b, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)} ${navY(Number(b.nav) || 0).toFixed(1)}`)
    .join(' ');

  // X-axis tick labels: ~6 evenly-spaced
  const tickIdxs = Array.from({ length: 6 }, (_, k) => Math.round((n - 1) * (k / 5)));

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="w-full h-[320px]"
      preserveAspectRatio="none"
    >
      {/* Y grid (cash) */}
      {[0, 0.25, 0.5, 0.75, 1].map((f) => {
        const y = padT + f * innerH;
        return (
          <line
            key={`g${f}`}
            x1={padL}
            x2={width - padR}
            y1={y}
            y2={y}
            stroke="rgba(255,255,255,0.05)"
            strokeWidth={1}
          />
        );
      })}

      {/* Zero line for cash */}
      <line
        x1={padL}
        x2={width - padR}
        y1={cashBaseline}
        y2={cashBaseline}
        stroke="rgba(255,255,255,0.2)"
        strokeWidth={1}
        strokeDasharray="3,3"
      />

      {/* Bars */}
      {series.map((b, i) => {
        const x = xAt(i) - barW / 2;
        const call = Number(b.capitalCall) || 0;
        const dist = Number(b.distribution) || 0;
        const callTop = cashY(call);
        const distTop = cashBaseline;
        const distBottom = cashY(-dist);
        return (
          <g key={i}>
            {call > 0 && (
              <rect
                x={x}
                y={callTop}
                width={barW}
                height={Math.max(0, cashBaseline - callTop)}
                fill="rgb(251 191 36)"
                opacity={0.8}
              />
            )}
            {dist > 0 && (
              <rect
                x={x}
                y={distTop}
                width={barW}
                height={Math.max(0, distBottom - distTop)}
                fill="rgb(52 211 153)"
                opacity={0.8}
              />
            )}
          </g>
        );
      })}

      {/* NAV line on its own scale */}
      <path
        d={navPath}
        fill="none"
        stroke="rgb(167 139 250)"
        strokeWidth={2.5}
        strokeLinejoin="round"
      />

      {/* Y-axis labels (cash) */}
      <text x={padL - 8} y={padT + 4} fill="rgb(148 163 184)" fontSize={10} textAnchor="end">
        {fmtMoney(cashMax)}
      </text>
      <text
        x={padL - 8}
        y={cashBaseline + 4}
        fill="rgb(148 163 184)"
        fontSize={10}
        textAnchor="end"
      >
        $0
      </text>
      <text
        x={padL - 8}
        y={padT + innerH + 4}
        fill="rgb(148 163 184)"
        fontSize={10}
        textAnchor="end"
      >
        {fmtMoney(cashMin)}
      </text>
      <text
        x={padL - 8}
        y={padT + 4}
        fill="rgb(148 163 184)"
        fontSize={9}
        textAnchor="end"
        dy={-10}
      >
        Cash
      </text>

      {/* Y-axis labels (NAV — right side) */}
      <text
        x={width - padR + 8}
        y={padT + 4}
        fill="rgb(167 139 250)"
        fontSize={10}
        textAnchor="start"
      >
        {fmtMoney(navMax)}
      </text>
      <text
        x={width - padR + 8}
        y={padT + innerH + 4}
        fill="rgb(167 139 250)"
        fontSize={10}
        textAnchor="start"
      >
        $0
      </text>
      <text
        x={width - padR + 8}
        y={padT + 4}
        fill="rgb(167 139 250)"
        fontSize={9}
        textAnchor="start"
        dy={-10}
      >
        NAV
      </text>

      {/* X-axis ticks */}
      {tickIdxs.map((i) => (
        <text
          key={`x${i}`}
          x={xAt(i)}
          y={height - 14}
          fill="rgb(148 163 184)"
          fontSize={10}
          textAnchor="middle"
        >
          {series[i]?.bucketDate?.substring(0, 7)}
        </text>
      ))}
    </svg>
  );
};

export default CashFlowForecastScreen;
